import { Globe, Mail, Phone, AtSign } from "lucide-react";
import { useContent } from "@/content";
import { Reveal } from "@/components/Motion";
import { NotifyForm, ContactForm } from "@/components/Forms";
import { V3Section, V3Title } from "@/sections/v3/shared";

// 12. Appel final — deux moitiés : infos à gauche, formulaires à droite.
const TeamLine = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-4 border-t border-copper/25 py-3.5 last:border-b">
    <Icon size={16} strokeWidth={1.2} className="shrink-0 text-copper" aria-hidden="true" />
    <span className="w-24 shrink-0 text-[0.75rem] uppercase tracking-[0.2em] text-white/70">{label}</span>
    <span className="ltr text-[0.95rem] text-white">{value}</span>
  </div>
);

export const FinalCall3 = () => {
  const c = useContent();
  const f = c.finalCall;
  const team = [
    { icon: Mail, label: f.teamLabels.email, value: f.team.email },
    { icon: Phone, label: f.teamLabels.phone, value: f.team.phone },
    { icon: AtSign, label: f.teamLabels.other, value: f.team.other },
  ].filter((t) => t.value);

  return (
    <V3Section id={f.id} bg="#7A0F1A" grain className="py-24 md:py-32">
      <div className="grid gap-16 lg:grid-cols-2 lg:gap-20">
        {/* Moitié gauche : dates clés, domaine, équipe */}
        <div>
          <V3Title testid="finalcall-title" className="mt-0 md:text-[2rem]">{f.title}</V3Title>
          <Reveal>
            <p className="mt-4 font-serif text-xl italic text-blush">{f.tagline}</p>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mt-12 text-[0.75rem] uppercase tracking-[0.3em] text-copper">{f.keyDatesTitle}</p>
          </Reveal>
          <div className="mt-4" data-testid="finalcall-keydates">
            {f.keyDates.map((kd) => (
              <div key={kd.label} className="flex items-baseline justify-between gap-6 border-t border-copper/25 py-4 last:border-b">
                <span className="text-[0.95rem] text-white/80">{kd.label}</span>
                <span className="font-serif text-[1.1rem] text-blush">{kd.value}</span>
              </div>
            ))}
          </div>

          <Reveal delay={0.12} className="mt-10">
            <p className="text-[0.95rem] text-white/80">
              {f.applyOn}{" "}
              <a
                href={c.site.applyUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="finalcall-domain"
                className="link-line font-serif text-[1.15rem] text-blush hover:text-white"
              >
                {c.site.applyDomain}
              </a>
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <p className="mt-12 flex items-center gap-3 text-[0.75rem] uppercase tracking-[0.3em] text-copper">
              <Globe size={15} strokeWidth={1.2} aria-hidden="true" />
              {f.teamTitle}
            </p>
          </Reveal>
          <div className="mt-4" data-testid="finalcall-team">
            {team.length ? (
              team.map((t) => <TeamLine key={t.label} {...t} />)
            ) : (
              <p className="border-t border-copper/25 py-4 text-[0.95rem] italic text-white/60">{f.teamPending}</p>
            )}
          </div>
        </div>

        {/* Moitié droite : formulaires existants */}
        <div className="space-y-14">
          <Reveal>
            <div className="border border-copper/30 p-7 md:p-10" data-testid="finalcall-notify">
              <h3 className="mb-8 font-serif text-[1.35rem] text-white">{c.forms.notify.title}</h3>
              <NotifyForm />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-copper/30 p-7 md:p-10" data-testid="finalcall-contact">
              <h3 className="mb-8 font-serif text-[1.35rem] text-white">{c.forms.contact.title}</h3>
              <ContactForm />
            </div>
          </Reveal>
        </div>
      </div>
    </V3Section>
  );
};
